import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  LogOut,
  User,
  FolderOpen,
  BookOpen,
  BarChart3,
  Database,
  Bug,
  ChevronDown,
  KeyRound,
  Check,
  Sparkles,
  Lightbulb,
  Loader2,
  Info,
  FlaskConical,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { useAuth } from "@/contexts/AuthContext";
import { useBackgroundTasks } from "@/contexts/BackgroundTaskContext";
import { EmbeddingProgressModal } from "@/components/EmbeddingProgressModal";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useMediaQuery";
import MobileBottomTabBar from "@/components/layout/MobileBottomTabBar";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { hasActiveEmbeddingTask, activeEmbeddingTasks, embeddingProgress, embeddingMessage } = useBackgroundTasks();
  const isMobile = useIsMobile();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [verboseLogging, setVerboseLogging] = useState(() => {
    return localStorage.getItem("verbose_logging") === "true";
  });
  const [languageChangeNotification, setLanguageChangeNotification] = useState<
    string | null
  >(null);

  const APP_VERSION = "3.0.9";

  // Update localStorage when verbose logging changes
  useEffect(() => {
    localStorage.setItem("verbose_logging", verboseLogging.toString());
    // Dispatch custom event so SessionDetailsPage can listen for changes
    window.dispatchEvent(
      new CustomEvent("verbose-logging-changed", { detail: verboseLogging }),
    );
  }, [verboseLogging]);


  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  // Navigation ordered: guidelines, manual, sessions, reports, catalog (admin), testing (admin), admin (admin)
  const navigation = [
    { name: "Guidelines", href: "/guidelines", icon: Lightbulb },
    { name: t("nav.manual"), href: "/manual", icon: BookOpen },
    { name: t("nav.sessions"), href: "/sessions", icon: FolderOpen },
    { name: t("nav.reports"), href: "/reports", icon: BarChart3 },
    ...(isAdmin
      ? [{ name: t("nav.catalog"), href: "/catalog", icon: Database }]
      : []),
    ...(isAdmin
      ? [{ name: "Testing", href: "/testing", icon: FlaskConical }]
      : []),
    ...(isAdmin ? [{ name: t("nav.admin"), href: "/admin", icon: Users }] : []),
  ];

  const changeLanguage = async (lng: string) => {
    if (lng === i18n.language) return; // Don't change if already selected

    // Store language preference with user ID for persistence
    const languageKey = user?.id ? `language_${user.id}` : "language_default";
    localStorage.setItem(languageKey, lng);

    // Change language
    await i18n.changeLanguage(lng);

    // Show visual feedback
    const languageName = lng === "en" ? "English" : "Français";
    setLanguageChangeNotification(`Language changed to ${languageName}`);

    // Hide notification after 2 seconds
    setTimeout(() => {
      setLanguageChangeNotification(null);
    }, 2000);
  };

  // Load user's language preference on component mount
  useEffect(() => {
    const languageKey = user?.id ? `language_${user.id}` : "language_default";
    const savedLanguage = localStorage.getItem(languageKey);
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [user, i18n]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm border-b z-50">
        <div
          className="mx-auto px-4 sm:px-6 lg:px-8"
          style={{ maxWidth: "104rem" }}
        >
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-serif font-semibold text-orange-600">
                    LUMIS
                  </span>
                  <span className="text-xs text-gray-500 -mt-1">
                    by AuthenTrace™
                  </span>
                </div>
              </Link>

            </div>

            <div className="flex items-center space-x-4">
              {/* Navigation - hidden on mobile, shown on md and up */}
              <nav className="hidden md:flex space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  // Enhanced active state detection
                  const isActive =
                    location.pathname === item.href ||
                    (item.href === "/sessions" &&
                      location.pathname.startsWith("/sessions/")) ||
                    (item.href === "/reports" &&
                      location.pathname.startsWith("/reports/")) ||
                    (item.href === "/testing" &&
                      location.pathname.startsWith("/testing/"));

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary-100 text-primary-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Embedding Progress Indicator (Admin Only) */}
              {isAdmin && hasActiveEmbeddingTask && (
                <div
                  onClick={() => setShowProgressModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md cursor-pointer hover:bg-blue-50 transition-colors border border-blue-200 bg-blue-50"
                >
                  <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-blue-900">
                      {Math.round(embeddingProgress * 100)}% Processing
                    </span>
                    {activeEmbeddingTasks.length > 1 && (
                      <span className="text-xs text-blue-600">
                        {activeEmbeddingTasks.length} tasks
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* User Menu */}
              <div
                className="relative border-l border-gray-200 pl-4"
                ref={dropdownRef}
              >
                <div
                  className="flex items-center space-x-2 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">
                    {user?.username}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 text-gray-500 transition-transform",
                      showUserDropdown && "rotate-180",
                    )}
                  />
                </div>

                {/* User Dropdown */}
                {showUserDropdown && (
                  <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg border z-[60]">
                    <div className="py-2">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <User className="h-8 w-8 bg-gray-200 rounded-full p-1.5 text-gray-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user?.username}
                            </p>
                            <span
                              className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                user?.role === "admin"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800",
                              )}
                            >
                              {user?.role}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {/* User Profile */}
                        <Link
                          to="/profile"
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setShowUserDropdown(false)}
                        >
                          <User className="h-4 w-4 mr-3 text-gray-500" />
                          User Profile
                        </Link>

                        <div className="my-1 border-t border-gray-100" />

                        {/* Verbose Logging Toggle */}
                        <button
                          onClick={() => setVerboseLogging(!verboseLogging)}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Bug
                            className={cn(
                              "h-4 w-4 mr-3",
                              verboseLogging
                                ? "text-green-600"
                                : "text-red-600",
                            )}
                          />
                          <span>{t("user_dropdown.debug")}</span>
                          <div
                            className={cn(
                              "ml-auto w-8 h-4 rounded-full flex items-center transition-colors",
                              verboseLogging ? "bg-green-500" : "bg-red-500",
                            )}
                          >
                            <div
                              className={cn(
                                "w-3 h-3 bg-white rounded-full transition-transform",
                                verboseLogging
                                  ? "translate-x-4"
                                  : "translate-x-0.5",
                              )}
                            />
                          </div>
                        </button>

                        {/* Version Info */}
                        <div className="px-4 py-2 text-xs text-gray-500 flex items-center">
                          <Info className="h-3 w-3 mr-2" />
                          Version {APP_VERSION}
                        </div>

                        {/* Separator */}
                        <div className="my-1 border-t border-gray-100" />

                        {/* Logout */}
                        <button
                          onClick={logout}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          {t("user_dropdown.logout")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className={cn(
          "mx-auto pt-20 px-4 sm:px-6 lg:px-8",
          isMobile ? "pb-20" : "pb-6" // Extra bottom padding on mobile for tab bar
        )}
        style={{ maxWidth: "104rem" }}
      >
        {children}
      </main>

      {/* Mobile Bottom Tab Bar */}
      <MobileBottomTabBar />

      {/* Language Change Notification */}
      {languageChangeNotification && (
        <div className="fixed top-20 right-4 z-50 transform transition-all duration-300 ease-in-out animate-in slide-in-from-right">
          <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
            <Check className="h-4 w-4" />
            <span className="text-sm font-medium">
              {languageChangeNotification}
            </span>
          </div>
        </div>
      )}

      {/* Embedding Progress Modal */}
      {isAdmin && (
        <EmbeddingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
        />
      )}
    </div>
  );
};

export default Layout;
